/**
 * TPG Playback Pipeline Tracer — Sprint 10 diagnostics.
 *
 * Temporary instrumentation. Emits ordered [TPG] console stages and retains
 * an in-memory ring buffer on window.__TPG_PIPELINE_TRACE__.
 *
 * Helpers:
 *   window.__TPG_DUMP__()        — stage summary
 *   window.__TPG_DUMP_AUDIO__()  — mute/volume/unlock events only
 *   window.__TPG_DUMP_PIP__()    — PiP / surface events only
 */

var STAGES = [
  "PAGE_LOADED",
  "HOST_INITIALIZED",
  "MEDIA_ASSET_RESOLVED",
  "INTRO_STARTED",
  "INTRO_MUTE_REQUEST",
  "INTRO_MUTED",
  "INTRO_ENDED",
  "HANDOFF_STARTED",
  "HANDOFF_COMPLETE",
  "PLAYER_REMOUNTED",
  "PLAYER_MOUNTED",
  "PLAYER_UNMOUNTED",
  "CONTROL_PLANE_BUILT",
  "SURFACE_ATTACHED",
  "SURFACE_AUDIT",
  "MAIN_PROVIDER_CREATED",
  "PROVIDER_SELECTED",
  "PROVIDER_INITIALIZED",
  "PROVIDER_READY",
  "PROVIDER_PLAY",
  "PROVIDER_MUTED",
  "PROVIDER_UNMUTED",
  "PROVIDER_VOLUME",
  "HOST_AUDIO_STATE",
  "SESSION_AUDIO_UNLOCKED",
  "PLAYBACK_STATE_MUTED",
  "FIRST_FRAME",
  "MEDIA_REQUEST_STARTED",
  "AUTOPLAY_REQUESTED",
  "PLAYBACK_STARTED",
  "PIP_REQUEST",
  "PIP_TARGET",
  "PIP_MODE",
  "PIP_SUPPORTED",
  "PIP_ENTER_SUCCESS",
  "PIP_ENTER_FAILURE",
  "PIP_EXIT",
  "CAPABILITIES_CHANGED",
  "ACTIVE_PROVIDER",
  "ACTIVE_SURFACE",
  "STATE",
  "ERROR",
];

var AUDIO_STAGES = {
  INTRO_STARTED: 1,
  INTRO_MUTED: 1,
  INTRO_MUTE_REQUEST: 1,
  INTRO_ENDED: 1,
  HANDOFF_STARTED: 1,
  HANDOFF_COMPLETE: 1,
  MAIN_PROVIDER_CREATED: 1,
  PROVIDER_READY: 1,
  PROVIDER_PLAY: 1,
  PROVIDER_MUTED: 1,
  PROVIDER_UNMUTED: 1,
  PROVIDER_VOLUME: 1,
  HOST_AUDIO_STATE: 1,
  SESSION_AUDIO_UNLOCKED: 1,
  PLAYBACK_STATE_MUTED: 1,
  FIRST_FRAME: 1,
  AUTOPLAY_REQUESTED: 1,
  PLAYBACK_STARTED: 1,
};

var PIP_STAGES = {
  PIP_REQUEST: 1,
  PIP_TARGET: 1,
  PIP_MODE: 1,
  PIP_SUPPORTED: 1,
  PIP_ENTER_SUCCESS: 1,
  PIP_ENTER_FAILURE: 1,
  PIP_EXIT: 1,
  CAPABILITIES_CHANGED: 1,
  ACTIVE_PROVIDER: 1,
  ACTIVE_SURFACE: 1,
  SURFACE_AUDIT: 1,
  SURFACE_ATTACHED: 1,
};

function ensureStore() {
  if (typeof window === "undefined") {
    return { events: [], startedAt: Date.now() };
  }
  if (!window.__TPG_PIPELINE_TRACE__) {
    window.__TPG_PIPELINE_TRACE__ = {
      events: [],
      startedAt: Date.now(),
      navigation:
        typeof performance !== "undefined" &&
        performance.getEntriesByType &&
        performance.getEntriesByType("navigation")[0]
          ? performance.getEntriesByType("navigation")[0].type
          : null,
      href: window.location && window.location.href,
    };
    window.__TPG_DUMP__ = function () {
      return tpgTraceDump();
    };
    window.__TPG_DUMP_AUDIO__ = function () {
      return tpgTraceFilterDump(AUDIO_STAGES, "AUDIO");
    };
    window.__TPG_DUMP_PIP__ = function () {
      return tpgTraceFilterDump(PIP_STAGES, "PIP");
    };
  }
  return window.__TPG_PIPELINE_TRACE__;
}

/**
 * @param {string} stage
 * @param {object} [detail]
 */
export function tpgTrace(stage, detail) {
  var store = ensureStore();
  var entry = {
    t: Date.now(),
    elapsedMs: Date.now() - store.startedAt,
    stage: stage,
    detail: detail || null,
  };
  store.events.push(entry);
  if (store.events.length > 800) {
    store.events.shift();
  }

  var suffix = "";
  if (detail != null) {
    try {
      if (typeof detail === "string" || typeof detail === "number") {
        suffix = ": " + detail;
      } else if (detail.provider) {
        suffix = ": " + detail.provider;
      } else if (detail.source) {
        suffix = ": " + String(detail.source).slice(0, 80);
      } else if (detail.message) {
        suffix = ": " + detail.message;
      } else {
        suffix = " " + JSON.stringify(detail).slice(0, 220);
      }
    } catch (e) {
      suffix = "";
    }
  }

  if (typeof console !== "undefined" && console.log) {
    console.log("[TPG] " + stage + suffix, detail != null ? detail : "");
  }
  return entry;
}

/**
 * Ordered stage names that have appeared (unique, first-seen order).
 * @returns {string[]}
 */
export function tpgTraceStagesReached() {
  var store = ensureStore();
  var seen = {};
  var out = [];
  for (var i = 0; i < store.events.length; i++) {
    var s = store.events[i].stage;
    if (!seen[s]) {
      seen[s] = true;
      out.push(s);
    }
  }
  return out;
}

/**
 * Dump a one-line summary of the pipeline so far.
 */
export function tpgTraceDump() {
  var stages = tpgTraceStagesReached();
  if (typeof console !== "undefined" && console.log) {
    console.log("[TPG] TRACE_DUMP stages=" + stages.join(" → "));
  }
  return stages;
}

/**
 * @param {object} stageSet
 * @param {string} label
 * @returns {object[]}
 */
export function tpgTraceFilterDump(stageSet, label) {
  var store = ensureStore();
  var rows = [];
  for (var i = 0; i < store.events.length; i++) {
    var e = store.events[i];
    if (stageSet[e.stage]) {
      rows.push(e);
    }
  }
  if (typeof console !== "undefined" && console.table) {
    console.log("[TPG] TRACE_DUMP_" + (label || "FILTER") + " n=" + rows.length);
    console.table(
      rows.map(function (r) {
        return {
          ms: r.elapsedMs,
          stage: r.stage,
          detail: r.detail ? JSON.stringify(r.detail).slice(0, 120) : "",
        };
      })
    );
  } else if (typeof console !== "undefined" && console.log) {
    console.log("[TPG] TRACE_DUMP_" + (label || "FILTER"), rows);
  }
  return rows;
}

/**
 * Build a compact surface audit payload for PiP / mute diagnostics.
 * @param {object} opts
 * @returns {object}
 */
export function tpgSurfaceAudit(opts) {
  opts = opts || {};
  var video = opts.video || null;
  var embed = opts.embed || null;
  var iframe =
    embed && embed.querySelector ? embed.querySelector("iframe") : null;
  return {
    provider: opts.providerName || null,
    surfaceKind: opts.surfaceKind || null,
    owningComponent: opts.owningComponent || "PlaybackEngine",
    hasVideoEl: !!video,
    videoSrc: video ? String(video.src || "").slice(0, 80) : null,
    videoCurrentSrc: video ? String(video.currentSrc || "").slice(0, 80) : null,
    videoSrcObject: !!(video && video.srcObject),
    videoDisplay: video && video.style ? video.style.display : null,
    videoMuted: video ? !!video.muted : null,
    videoPaused: video ? !!video.paused : null,
    videoReadyState: video ? video.readyState : null,
    hasEmbedRoot: !!embed,
    embedDisplay: embed && embed.style ? embed.style.display : null,
    hasIframe: !!iframe,
    iframeSrc: iframe ? String(iframe.src || "").slice(0, 100) : null,
    pipTarget: opts.pipTarget || null,
    pipMode: opts.pipMode || null,
    fullscreenTarget: opts.fullscreenTarget || null,
    muteTarget: opts.muteTarget || null,
    volumeTarget: opts.volumeTarget || null,
    pictureInPictureEnabled:
      typeof document !== "undefined" ? !!document.pictureInPictureEnabled : null,
    webkitPiP:
      !!(video && typeof video.webkitSetPresentationMode === "function"),
  };
}

export var TpgPipelineStage = STAGES.reduce(function (acc, s) {
  acc[s] = s;
  return acc;
}, {});

export default {
  tpgTrace: tpgTrace,
  tpgTraceDump: tpgTraceDump,
  tpgTraceStagesReached: tpgTraceStagesReached,
  tpgTraceFilterDump: tpgTraceFilterDump,
  tpgSurfaceAudit: tpgSurfaceAudit,
  TpgPipelineStage: TpgPipelineStage,
};
